import { Asset, AssetDetails, TotalInfo, TotalInfoNullable } from '../../api';
import { v1 as uuid } from 'uuid';
import { Page } from '../router/model';
import { MouseEvent } from 'react';
import { generatePath } from 'react-router';
import { hubRoutes } from '../../../general/urls';
import { CachedRecord } from '../../cache';

export type FinderShape = {
  selected: FinderSelected;
  containerWidth: number;
  details: { asset: AssetDetails | undefined; error: boolean | number };
  detailsLoading: boolean;
  detailsVisible: boolean;
  slotHeight: number | undefined;
  slotDragging: boolean;
  isOriginalView: boolean;
};

export type FinderSelected = {
  ids: Asset['id'][];
  firstId: Asset['id'] | undefined; // с чего начался процесс выделения
  lastId: Asset['id'] | undefined; // где закончился процесс выделения
};

// Для текущего режима finder нам нужен более узкий тип Mode вместо более широкого Page.
// Это нужно для того, чтобы можно было писать конструкции вида Record<Mode, x>,
// тем самым гарантируя, что для каждого режима в finder обязательно есть некий "x".
// Под "x" подразумеваются: ассеты, экшен запроса ассетов, состояние загрузки, т.д.
// Если потом мы захотим добавить еще один элемент в MODES,
// то TS подсветит все места с Record<Mode, x>, чтобы мы также не забыли их дополнить.
export { Page } from '../router/model';
export const MODES = [Page.viewer, Page.pack, Page.favorite, Page.sharing] as const;
export type Mode = (typeof MODES)[number];

export function newScrollHash() {
  return uuid();
}

export enum LoadingMode {
  // Различные запросы на загрузку ассетов (в сагах)
  refresh = 'refresh',
  nextPage = 'nextPage',
}

export enum Loading {
  // Различные отображения состояния загрузки (в интерфейсе)
  none,
  refresh,
  nextPage,
}

// Геометрия сетки ассетов. Значения повторяют SCSS (.al-finder-asset), но нужны и в JS:
// по ним считается раскладка виртуализированного списка и скелетон сетки. Скелетон обязан
// совпадать с реальными карточками пиксель в пиксель (иначе при появлении контента вёрстка
// дёрнется), поэтому источник должен быть один — раньше эти числа лежали в двух файлах.
export const SCROLLBAR_WIDTH = 8; // вертикальный скроллбар списка (задан в общих стилях)
export const ASSET_CARD_PADDING = 5; // .al-finder-asset { padding }
export const ASSET_IMAGE_SLOT_HEIGHT = 128; // .al-finder-asset__image-slot { height }
export const CREATIVE_IMAGE_SLOT_HEIGHT = 160; // .al-finder-asset_creative — слот выше

// Кэш загруженных списков ассетов по ключу фильтра.
// Позволяет мгновенно показывать ранее загруженную папку/раздел при повторном заходе
// (данные берутся из кэша, а свежий запрос уходит в фоне без спиннера), см. loadWithPagination.
// Общая часть записи (storedAt/revalidate) и правило устаревания — в assetLab/cache.ts.
export type CacheEntry<D> = CachedRecord & {
  data: D;
  hasNextPage: boolean;
  scrollHash: string;
  // Сколько ассетов вернул бэкенд на первой странице этой локации. Не угадываем: размер
  // страницы задаёт сервер, а персисту нужно знать, где заканчивается первая страница.
  pageSize?: number;
};

export type AssetsCache<D> = Record<string, CacheEntry<D>>;

// Максимальное число закэшированных локаций (защита от неограниченного роста в долгой
// сессии). Запись — это весь список локации со всеми подгруженными страницами, поэтому
// держим столько же, сколько переживает перезагрузку (см. MAX_ENTRIES в persistentCache):
// практически полезны последние несколько локаций, остальное — балласт в памяти.
export const CACHE_MAX_ENTRIES = 20;

export function isLikeCtrlKey(event: MouseEvent | KeyboardEvent) {
  const isMac = window.navigator.platform.toLowerCase().includes('mac');
  return isMac ? event.metaKey : event.ctrlKey;
}

export const EMPTY_TOTAL: TotalInfo = {
  read: { count: 0, size: 0 },
  download: { count: 0, size: 0 },
};

export function applyTotal(total: TotalInfo, totalNullable: TotalInfoNullable) {
  type Rule = keyof TotalInfoNullable;
  type Key = keyof TotalInfoNullable[Rule];
  (Object.keys(totalNullable) as Rule[]).forEach((rule) => {
    (Object.keys(totalNullable[rule]) as Key[]).forEach((key) => {
      const value = totalNullable[rule][key];
      if (value !== null) {
        total[rule][key] = value;
      }
    });
  });
}

export function creativeHubExternalURLRaw(
  folderID: number | string,
  fileID?: number | string,
  preview?: boolean,
) {
  if (fileID !== undefined) {
    if (preview) {
      return (
        generatePath(hubRoutes.fileExternal, { folderID, fileID }) + `?highlight_node_id=${fileID}`
      );
    }
    return generatePath(hubRoutes.folderExternal, { folderID }) + `?highlight_node_id=${fileID}`;
  }
  return generatePath(hubRoutes.folderExternal, { folderID });
}
export function creativeHubExternalURL(details: AssetDetails) {
  const file = details.path.split('.').at(-1) ?? 0;
  const folder = details.path.split('.').at(-2) ?? 0;
  return creativeHubExternalURLRaw(folder, file, true);
}
